import { ReactNode, useCallback, useEffect, useState } from 'react';
import { DataCacheContext } from '../../contexts';

interface DataCacheProviderProps {
  children: ReactNode;
}

type WorkerEndpointMap = Record<string, Worker>;

export default function DataCacheProvider({
  children,
}: Readonly<DataCacheProviderProps>) {
  const [sse, setSse] = useState<EventSource | null>(null);

  useEffect(() => {
    setSse(
      new EventSource(
        import.meta.env.VITE_REACT_APP_API_HOST +
          import.meta.env.VITE_REACT_APP_PUB_DIRECTORY
      )
    );
  }, []);

  const [dataCache, setDataCache] = useState<WorkerEndpointMap>({});

  const startWorkerUpdater = useCallback(
    (endpoint: string, updateDelay: number, usefakedata?: boolean) => {
      const updateWorker = new Worker('/updateWorker.js');
      let apiUrl = import.meta.env.VITE_REACT_APP_API_HOST + '/' + endpoint;
      if (usefakedata) {
        apiUrl = '/fakedata/' + endpoint + '.json';
      }

      updateWorker.postMessage({
        msg: 'connect',
        apiUrl: apiUrl,
        type: endpoint,
        updateDelay: updateDelay,
      });
      if (sse) {
        sse.addEventListener(endpoint, (e) => {
          updateWorker.postMessage({ msg: 'update', data: e.data });
        });
      } else {
        console.warn('SSE is not initialized for ' + endpoint);
      }
      return updateWorker;
    },
    [sse]
  );

  const createDataCache = useCallback(
    (
      endpoint: string,
      updateDelay: number,
      objectsListener: (e: MessageEvent) => void,
      useFakeData?: boolean
    ) => {
      const newDataCache = { ...dataCache };
      if (newDataCache[endpoint]) {
        newDataCache[endpoint].addEventListener('message', function (e) {
          objectsListener(e);
        });
        newDataCache[endpoint].postMessage({ msg: 'resume' });
        return newDataCache;
      } else {
        setDataCache((dataCache) => {
          const newDataCache = { ...dataCache };
          newDataCache[endpoint] = startWorkerUpdater(
            endpoint,
            updateDelay,
            useFakeData
          );
          newDataCache[endpoint].addEventListener(
            'message',
            function (e: MessageEvent) {
              objectsListener(e);
            }
          );
          newDataCache[endpoint].postMessage({ msg: 'resume' });
          return newDataCache;
        });
      }
    },
    [dataCache, startWorkerUpdater]
  );

  const contextValue = { createDataCache: createDataCache, sse: sse };

  return (
    <DataCacheContext.Provider value={contextValue}>
      {children}
    </DataCacheContext.Provider>
  );
}
